<template lang="pug">
  .container
    .row
      ////// LEFT CONTENT
      .col-lg-9.col-md-8
        transition(name="fade")
          a.banner.banner--infos.banner--white(:href="homeCareAppointmentPath" v-if="homeCareAppointmentPath && homeCareAvailable")
            .info
              i.fal.fa-info
            .banner__title
              span {{ $t('components.search_result.eligible') }}
              span {{ $t('components.search_result.home_care') }}
            .banner__action
              .btn.btn-primary
                span {{ $t('components.search_result.make_appointment') }}

        .banner.banner--m-centered(v-show="!loading")
          .h4 {{ $t('components.search_result.results') }}
          .h6.m-0 {{ $tc('components.search_result.practitioner', totalEntries, {count: totalEntries}) }}

        slot

        p(v-show="results.length === 0 && !loading")
          span Nous n'avons pas d'ostéopathe dans votre secteur
          span(v-show="otherResults.length")|, voici les plus proches de vous correspondant à votre recherche :


        .spinner-wrapper(v-show="loading")
          .fal.fa-2x.fa-spinner.fa-spin

        transition-group(name="fade" class="row")
          .col-lg-4(v-for="result in searchResults" :key="result.id")
            result-card(
              v-show="!loading"
              :result='result.attributes.practitioner'
              :active="isActiveMarker(result.attributes.office_id) && activeCards.includes(result.id)"
              :office-id="result.attributes.office_id"
              :office-name="result.attributes.office_name"
              :office-address="result.attributes.office_address"
              @hover="activeMarkers = [result.attributes.office_id]; activeCards = [result.id]"
              @unhover="activeMarkers = []; activeCards = []"
              :distance="result.attributes.distance"
            )

        pagination(v-show="results.length" @pageChanged="fetchResults" :totalPages="totalPages")

      ///// RIGHT CONTENT
      .col-lg-3.col-md-4
        gmap-map(
          :center="{lat: Number(coordinates.latitude), lng: Number(coordinates.longitude)}"
          :zoom="zoom"
          :options="optionsMap"
          ref="googleMap"
        )
          gmap-cluster(:zoomOnClick="true" :maxZoom="18" :styles="this.$root.clusterStyles")
            gmap-marker(
              v-for="marker in officeMarkers"
              v-if="!!marker.office.attributes.office_address"
              :key="marker.office.id"
              @click="setSelectedResult(marker.office)"
              :options="{optimized: false}"
              :icon="isActiveMarker(marker.office.attributes.office_id) ? { url: require('images/map/map-marker-active.png')} : { url: require('images/map/map-marker.png')} "
              :position='marker.position'
            )

</template>

<script>
  /* eslint no-undef: 0 */

  import ResultCard from '@appComponents/ResultCard'
  import Pagination from "@sharedComponents/Pagination"
  import axios from 'axios'
  import randomSeed from 'random-seed'
  import { merge, filter } from 'lodash'

  export default {
    props: {
      resourceUrl: String,
      formattedAddress: String,
      coordinates: Object,
      homeCareAppointmentPath: String
    },
    components: { ResultCard, Pagination },
    data () {
      return {
        zoom: 12,
        activeCards: [],
        activeMarkers: [],
        loading: false,
        totalPages: undefined,
        totalEntries: undefined,
        homeCareAvailable: false,
        results: [],
        otherResults: [],
        optionsMap: merge(this.$root.configMap, {})
      }
    },
    methods: {
      isActiveMarker (resultId) {
        return this.activeMarkers.includes(resultId)
      },

      fetchResults (page = 1) {
        this.loading = true
        this.results = []
        axios.get(this.resourceUrl, { params: {
          location: this.formattedAddress,
          latitude: this.coordinates.latitude,
          longitude: this.coordinates.longitude,
          by_published: "true",
          per_page: 12,
          page: page
        }}).then(response => {
          if (response.data.data.data.length) {
            this.totalPages = response.data.data.total_pages
            this.totalEntries = response.data.data.total_entries
            this.homeCareAvailable = response.data.home_care_available
            this.results = response.data.data.data
            this.loading = false
          } else {
            this.fetchOtherResults()
          }
        })
      },

      fetchOtherResults () {
        this.loading = true
        this.otherResults = []
        axios.get(this.resourceUrl, { params: {
          location: this.formattedAddress,
          latitude: this.coordinates.latitude,
          longitude: this.coordinates.longitude,
          distance_in_km: 100,
          by_published: "true",
          per_page: 9,
          page: 1
        }}).then(response => {
          this.otherResults = response.data.data.data
          this.totalEntries = response.data.data.total_entries
          this.homeCareAvailable = response.data.home_care_available
          this.loading = false
        })
      },

      setSelectedResult (result) {
        this.activeCards = filter(this.searchResults, { attributes: { office_id: result.attributes.office_id } }).map(po => po.id)
        this.activeMarkers = [result.attributes.office_id]
        this.$nextTick(function () {
          this.$scrollTo(".thumb--profile.active", 500, { offset: -200 })
        })
      }
    },
    watch: {
      officeMarkers () {
        if (this.officeMarkers.length) {
          this.$refs.googleMap.$mapPromise.then(() => {
            const google = window.google
            let bounds = new google.maps.LatLngBounds()
            this.officeMarkers.forEach((marker) => {
              let position = new google.maps.LatLng(marker.position.lat, marker.position.lng)
              bounds.extend(position)
            })
            this.$refs.googleMap.fitBounds(bounds)
          })
        }
      },

      totalEntries () {
        const resultCount = document.querySelector("#result-count")
        resultCount.innerHTML = this.$tc('components.search_result.practitioner', this.totalEntries, { count: this.totalEntries })
        resultCount.parentElement.style.display = "block"
      }
    },
    computed: {
      searchResults () {
        if (this.results.length) {
          return this.results
        } else {
          return this.otherResults
        }
      },

      officeMarkers () {
        return this.results.map((office) => {
          let address = office.attributes.office_address

          // Use a seeded random generator to always have the same placement for each marker
          let rand = randomSeed.create()
          rand.seed(address.id)

          if (address) {
            let latOffset = (rand(100) - 50) / 1000000
            let lngOffset = (rand(200) - 100) / 1000000
            return {
              office,
              position: {
                lat: address.attributes.latitude + latOffset,
                lng: address.attributes.longitude + lngOffset
              }
            }
          }
        })
      }
    },

    mounted () {
      if (this.coordinates.latitude && this.coordinates.longitude) {
        this.fetchResults()
      }
    }
  }
</script>

<style>
.spinner-wrapper {
  width: 100%;
  text-align: center;
}

.map,
.vue-map-container {
  top: 10%;
  position: sticky !important;
}
</style>

